import { useCallback, useRef, useState } from 'react';

export interface ZoomPanState {
  scale: number;
  translateX: number;
  translateY: number;
}

export interface UseImageZoomPanOptions {
  minScale?: number;
  maxScale?: number;
  /** Scale applied by double-click/double-tap when at 1x (default 2.5) */
  doubleClickScale?: number;
}

const DEFAULT_MIN_SCALE = 1;
const DEFAULT_MAX_SCALE = 8;
const DEFAULT_DOUBLE_CLICK_SCALE = 2.5;

export const clampScale = (scale: number, min: number, max: number): number => Math.min(max, Math.max(min, scale));

/**
 * Compute the translation that keeps the point under the cursor stationary
 * while the scale changes (zoom-to-cursor). Coordinates are relative to the
 * container center, matching a `translate(tx, ty) scale(s)` CSS transform.
 */
export const zoomAroundPoint = (state: ZoomPanState, nextScale: number, pointX: number, pointY: number): ZoomPanState => {
  const ratio = nextScale / state.scale;
  return {
    scale: nextScale,
    translateX: pointX - (pointX - state.translateX) * ratio,
    translateY: pointY - (pointY - state.translateY) * ratio,
  };
};

const IDENTITY: ZoomPanState = { scale: 1, translateX: 0, translateY: 0 };

interface ActivePointer {
  id: number;
  x: number;
  y: number;
}

/**
 * Zoom + pan gesture handling for an image inside a container element.
 *
 * Supports:
 * - mouse wheel / trackpad zoom, anchored at the cursor
 * - pinch zoom via pointer events (touch)
 * - drag-to-pan while zoomed (mouse or single touch)
 * - double-click / double-tap to toggle between 1x and doubleClickScale
 *
 * Presentation-agnostic: returns handlers to spread on the container and the
 * current transform; the caller owns all rendering. Reset state by calling
 * `reset()` (e.g. when the image source changes).
 */
export function useImageZoomPan(options: UseImageZoomPanOptions = {}) {
  const minScale = options.minScale ?? DEFAULT_MIN_SCALE;
  const maxScale = options.maxScale ?? DEFAULT_MAX_SCALE;
  const doubleClickScale = options.doubleClickScale ?? DEFAULT_DOUBLE_CLICK_SCALE;

  const [state, setState] = useState<ZoomPanState>(IDENTITY);
  // Pointer tracking lives in refs: gesture bookkeeping must not re-render.
  const pointers = useRef<Map<number, ActivePointer>>(new Map());
  const pinchStartDistance = useRef<number | null>(null);
  const pinchStartScale = useRef(1);
  const isPanning = useRef(false);
  const didPan = useRef(false);

  const reset = useCallback(() => {
    pointers.current.clear();
    pinchStartDistance.current = null;
    isPanning.current = false;
    didPan.current = false;
    setState(IDENTITY);
  }, []);

  /** Point relative to the container center (transform origin). */
  const relativePoint = (e: { clientX: number; clientY: number; currentTarget: EventTarget & Element }) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: e.clientX - rect.left - rect.width / 2,
      y: e.clientY - rect.top - rect.height / 2,
    };
  };

  const applyScale = useCallback(
    (nextScaleRaw: number, pointX: number, pointY: number) => {
      setState((prev) => {
        const nextScale = clampScale(nextScaleRaw, minScale, maxScale);
        if (nextScale === prev.scale) return prev;
        const next = zoomAroundPoint(prev, nextScale, pointX, pointY);
        // Snap back to a clean identity at minimum zoom so the image re-centers.
        return next.scale <= minScale ? { ...IDENTITY, scale: minScale } : next;
      });
    },
    [minScale, maxScale]
  );

  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLElement>) => {
      e.preventDefault();
      const { x, y } = relativePoint(e);
      const factor = Math.exp(-e.deltaY * 0.0015);
      setState((prev) => {
        const nextScale = clampScale(prev.scale * factor, minScale, maxScale);
        if (nextScale === prev.scale) return prev;
        const next = zoomAroundPoint(prev, nextScale, x, y);
        return next.scale <= minScale ? { ...IDENTITY, scale: minScale } : next;
      });
    },
    [minScale, maxScale]
  );

  const onDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      const { x, y } = relativePoint(e);
      setState((prev) => {
        if (prev.scale > minScale) return { ...IDENTITY, scale: minScale };
        return zoomAroundPoint(prev, clampScale(doubleClickScale, minScale, maxScale), x, y);
      });
    },
    [minScale, maxScale, doubleClickScale]
  );

  const pinchDistance = () => {
    const pts = Array.from(pointers.current.values());
    if (pts.length < 2) return null;
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  };

  // Latest state for pointer handlers without re-binding them on every zoom.
  const stateRef = useRef(state);
  stateRef.current = state;

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { id: e.pointerId, x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      pinchStartDistance.current = pinchDistance();
      pinchStartScale.current = stateRef.current.scale;
      isPanning.current = false;
    } else if (pointers.current.size === 1) {
      isPanning.current = true;
      didPan.current = false;
    }
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const tracked = pointers.current.get(e.pointerId);
      if (!tracked) return;
      const prevX = tracked.x;
      const prevY = tracked.y;
      tracked.x = e.clientX;
      tracked.y = e.clientY;

      if (pointers.current.size === 2 && pinchStartDistance.current) {
        // Pinch zoom anchored at the midpoint of the two touches
        const dist = pinchDistance();
        if (!dist) return;
        const pts = Array.from(pointers.current.values());
        const rect = e.currentTarget.getBoundingClientRect();
        const midX = (pts[0].x + pts[1].x) / 2 - rect.left - rect.width / 2;
        const midY = (pts[0].y + pts[1].y) / 2 - rect.top - rect.height / 2;
        applyScale(pinchStartScale.current * (dist / pinchStartDistance.current), midX, midY);
        return;
      }

      if (isPanning.current && stateRef.current.scale > minScale) {
        const dx = e.clientX - prevX;
        const dy = e.clientY - prevY;
        if (dx !== 0 || dy !== 0) {
          didPan.current = true;
          setState((prev) => ({ ...prev, translateX: prev.translateX + dx, translateY: prev.translateY + dy }));
        }
      }
    },
    [applyScale, minScale]
  );

  const onPointerUpOrCancel = useCallback((e: React.PointerEvent<HTMLElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) {
      pinchStartDistance.current = null;
    }
    if (pointers.current.size === 0) {
      isPanning.current = false;
    }
  }, []);

  /**
   * True when the last pointer interaction included a pan drag. Callers that
   * close on background click use this to ignore clicks that end a drag.
   */
  const consumeDidPan = useCallback(() => {
    const value = didPan.current;
    didPan.current = false;
    return value;
  }, []);

  const zoomIn = useCallback(() => applyScale(stateRef.current.scale * 1.5, 0, 0), [applyScale]);
  const zoomOut = useCallback(() => applyScale(stateRef.current.scale / 1.5, 0, 0), [applyScale]);

  return {
    scale: state.scale,
    translateX: state.translateX,
    translateY: state.translateY,
    isZoomed: state.scale > minScale,
    /** CSS transform for the image element */
    transform: `translate(${state.translateX}px, ${state.translateY}px) scale(${state.scale})`,
    containerHandlers: {
      onWheel,
      onDoubleClick,
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerUpOrCancel,
      onPointerCancel: onPointerUpOrCancel,
      onPointerLeave: onPointerUpOrCancel,
    },
    zoomIn,
    zoomOut,
    reset,
    consumeDidPan,
  };
}
